/**
 * ApplicationLogsTab Component
 *
 * Displays application logs in a DataTable with search, filters, and live updates
 * Pattern: Similar to RolesTab from Permissions page
 */

import { useState, useMemo, useCallback } from 'react';
import { IconWifi, IconSearch, IconFilter, IconEye, IconX } from '@tabler/icons-react';
import { Stack, Card, Group, Button, Select, MultiSelect, TextInput, Badge, Text, Code, ActionIcon, Modal, Divider, ScrollArea } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { ApplicationLogsLiveModal } from './LiveUpdatesModal';
import type { ApplicationLogsTabProps, AppLogRow } from '../types';
import type { AppLogDto } from '@actschurch/ccms-sdk';

// Helper: Format timestamp
const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Helper: Get log level color with distinct colors
const getLogLevelColor = (level: string): string => {
  switch (level.toLowerCase()) {
    case 'critical':
      return 'red';
    case 'error':
      return 'pink';
    case 'warning':
      return 'orange';
    case 'information':
    case 'info':
      return 'cyan';
    case 'debug':
      return 'grape';
    default:
      return 'gray';
  }
};

export function ApplicationLogsTab({ logs, onRefresh, loading }: ApplicationLogsTabProps) {
  const [liveModalOpened, setLiveModalOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterServices, setFilterServices] = useState<string[]>([]);
  const [selectedLog, setSelectedLog] = useState<AppLogDto | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Get unique services for filter dropdown
  const services = useMemo(() => {
    const serviceSet = new Set<string>();
    logs.forEach((log) => {
      if (log.serviceName) serviceSet.add(log.serviceName);
    });
    return Array.from(serviceSet).sort();
  }, [logs]);

  // Filter logs based on search and filters
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      // Level filter
      if (filterLevel && log.logLevel !== filterLevel) return false;

      // Service filter (multi-select)
      if (filterServices.length > 0 && !filterServices.includes(log.serviceName || '')) return false;

      // Search filter (message, category, correlation ID)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesMessage = log.message?.toLowerCase().includes(query);
        const matchesCategory = log.category?.toLowerCase().includes(query);
        const matchesCorrelation = log.correlationId?.toLowerCase().includes(query);
        return matchesMessage || matchesCategory || matchesCorrelation;
      }

      return true;
    });
  }, [logs, filterLevel, filterServices, searchQuery]);

  // Convert to row format (ensure non-nullable id)
  const rows: AppLogRow[] = useMemo(() => {
    return filteredLogs.map((log) => ({
      ...log,
      id: log.id,
    }));
  }, [filteredLogs]);

  // Handle view details
  const handleViewDetails = useCallback((log: AppLogDto) => {
    setSelectedLog(log);
  }, []);

  // Define columns - Simplified table view
  const columns: DataTableColumn<AppLogRow>[] = useMemo(
    () => [
      {
        key: 'timestamp',
        label: 'Timestamp',
        sortable: true,
        width: '180px',
        render: (value) => (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {formatTimestamp(value as string)}
          </Text>
        ),
      },
      {
        key: 'logLevel',
        label: 'Level',
        width: '120px',
        render: (value) => (
          <Badge color={getLogLevelColor(value as string)} size="sm" variant="filled" style={{ minWidth: '80px', textAlign: 'center' }}>
            {value as string}
          </Badge>
        ),
      },
      {
        key: 'serviceName',
        label: 'Service',
        width: '150px',
        render: (value) => (
          <Text size="sm" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(value as string) || 'N/A'}</Text>
        ),
      },
      {
        key: 'message',
        label: 'Message',
        width: '40%',
        render: (value) => (
          <Text size="sm" lineClamp={3} style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%' }}>
            {value as string}
          </Text>
        ),
      },
      {
        key: 'actions',
        label: '',
        width: '60px',
        render: (_, row) => (
          <ActionIcon
            variant="light"
            size="sm"
            color="blue"
            onClick={() => handleViewDetails(row)}
            aria-label="View log details"
          >
            <IconEye size={14} />
          </ActionIcon>
        ),
      },
    ],
    [handleViewDetails]
  );

  return (
    <Stack gap="md">
      {/* Actions and Filters */}
      <Card withBorder padding="md">
        <Stack gap="md">
          <Group gap="md">
            <Button
              leftSection={<IconWifi size={16} />}
              onClick={() => setLiveModalOpened(true)}
              variant="gradient"
              gradientVariant="primary"
            >
              Live Updates
            </Button>
            <Button
              variant="light"
              onClick={onRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>

          <Group gap="md">
            <Select
              placeholder="Filter by level"
              value={filterLevel}
              onChange={(value) => setFilterLevel(value || '')}
              data={[
                { value: '', label: 'All Levels' },
                { value: 'Critical', label: 'Critical' },
                { value: 'Error', label: 'Error' },
                { value: 'Warning', label: 'Warning' },
                { value: 'Information', label: 'Information' },
                { value: 'Debug', label: 'Debug' },
              ]}
              leftSection={<IconFilter size={16} />}
              style={{ minWidth: 200 }}
            />
            <MultiSelect
              placeholder="Filter by service"
              value={filterServices}
              onChange={setFilterServices}
              data={services.map((s) => ({ value: s, label: s }))}
              leftSection={<IconFilter size={16} />}
              clearable
              style={{ minWidth: 280 }}
            />
            <TextInput
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftSection={<IconSearch size={16} />}
              style={{ flex: 1 }}
            />
          </Group>
        </Stack>
      </Card>

      {/* DataTable */}
      <Card padding="lg" style={{ tableLayout: 'fixed' }}>
        <DataTable<AppLogRow>
          data={rows}
          columns={columns}
          pagination={{
            page,
            pageSize,
            total: rows.length,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
          }}
          gradientVariant="primary"
          highlightOnHover
          striped
          stickyHeader
          withRowAnimations
          emptyMessage="No application logs found. Try adjusting your filters."
          enableInternalSort
          defaultSortKey="timestamp"
          defaultSortDirection="desc"
        />
      </Card>

      <Text size="sm" c="dimmed">
        Showing {rows.length} of {logs.length} logs
      </Text>

      {/* Live Updates Modal */}
      <ApplicationLogsLiveModal
        opened={liveModalOpened}
        onClose={() => setLiveModalOpened(false)}
        serviceNames={filterServices.length > 0 ? filterServices : undefined}
      />

      {/* Log Detail Modal */}
      <Modal
        opened={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        title="Log Details"
        size="lg"
        variant="elevated"
      >
        {selectedLog && (
          <Stack gap="md">
            {/* Header Info */}
            <Group gap="sm">
              <Badge color={getLogLevelColor(selectedLog.logLevel)} size="md" variant="filled">
                {selectedLog.logLevel}
              </Badge>
              <Text size="sm" c="dimmed">
                {formatTimestamp(selectedLog.timestamp)}
              </Text>
            </Group>

            <Divider />

            {/* Service and Category */}
            <Group gap="xl">
              <div>
                <Text size="xs" c="dimmed" fw={600}>
                  Service
                </Text>
                <Text size="sm">{selectedLog.serviceName || 'N/A'}</Text>
              </div>
              <div>
                <Text size="xs" c="dimmed" fw={600}>
                  Category
                </Text>
                <Text size="sm">{selectedLog.category}</Text>
              </div>
              {selectedLog.correlationId && (
                <div>
                  <Text size="xs" c="dimmed" fw={600}>
                    Correlation ID
                  </Text>
                  <Code size="sm">{selectedLog.correlationId}</Code>
                </div>
              )}
            </Group>

            <Divider />

            {/* Message */}
            <div>
              <Text size="xs" c="dimmed" fw={600} mb="xs">
                Message
              </Text>
              <Card withBorder p="md" style={{ backgroundColor: 'var(--mantine-color-gray-light)' }}>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {selectedLog.message}
                </Text>
              </Card>
            </div>

            {/* Exception (if exists) */}
            {selectedLog.exception && (
              <div>
                <Text size="xs" c="dimmed" fw={600} mb="xs">
                  Exception
                </Text>
                <ScrollArea h={200}>
                  <Card withBorder p="md" style={{ backgroundColor: 'var(--mantine-color-red-light)' }}>
                    <Code block style={{ fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {selectedLog.exception}
                    </Code>
                  </Card>
                </ScrollArea>
              </div>
            )}

            {/* Close Button */}
            <Group justify="flex-end">
              <Button
                variant="light"
                onClick={() => setSelectedLog(null)}
                leftSection={<IconX size={16} />}
              >
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
